import { ClientEvents } from 'common/events/ClientEvents';
import { useCallback, useContext, useState } from 'react';
import { SubscriptionStatus, useConsumerUpdateSubscriptionMutation } from 'src/gqlReactTypings.generated.d';
import {
  OrdersCard,
  OrdersListRowButton,
  OrdersListRowButtonContainer,
} from 'src/pages/consumer/account/orders/styles';
import { Pencil } from 'src/shared/design-system/Icon/Pencil';

import { ButtonColors } from 'src/shared/design-system/Buttons';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Notifications } from 'src/shared/Notifications';
import { Routes } from 'src/shared/Routes';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { Colors, Spacing } from 'web-common/src/shared/styles';
import { MealPlanSubscriptionContext } from '../../meal-plans/MealPlanSubscriptionsContext';
import { ConsumerOrdersCardBody } from './ConsumerOrdersCardBody';
import { ConsumerSubscriptionsCardHeader } from './ConsumerSubscriptionsCardHeader';
import { RecurringSubscription } from './utilities';

const CancelButtonStyled = styled(OrdersListRowButton).attrs(() => ({ color: ButtonColors.WHITE }))`
  color: ${ifProp('disabled', Colors.GRAY_DARK_10, Colors.BLACK)};
  border: 2px solid ${ifProp('disabled', Colors.GRAY_DARK_10, Colors.RADISH)};
`;

const EditButtonStyled = styled(OrdersListRowButton).attrs(() => ({ color: ButtonColors.WHITE }))`
  color: ${Colors.GRAY_DARK_30};
  border: 2px solid ${Colors.RADISH};
`;

const EditButtonIcon = styled(Pencil)`
  width: ${Spacing.SINGLE};
  margin-right: ${Spacing.QUARTER};
`;

export const ConsumerSubscriptionsCard = ({ subscription }: { subscription: RecurringSubscription }) => {
  const isMealPlanOrder = subscription.orderTemplate.publicShef.publicName.includes('Meal Plan');
  const { refetchMealPlanData } = useContext(MealPlanSubscriptionContext);
  const history = useHistory();
  const [disabled, setDisabled] = useState(false);
  const [updateSubscription, { loading }] = useConsumerUpdateSubscriptionMutation();
  const tracker = useTracker();

  const doCancel = useCallback(async () => {
    const { errors } = await updateSubscription({
      variables: {
        input: { id: subscription.id, status: SubscriptionStatus.Canceled },
      },
    });

    tracker.track(ClientEvents.RECURRING_SUBSCRIPTION_CANCEL_BUTTON_CLICK, { subscriptionId: subscription.id });

    if (errors) {
      Notifications.error('We were unable to cancel your subscription. Please contact support');
      console.error('Failed to cancel subscription.', errors);
    } else {
      Notifications.success('Subscription successfully cancelled!');
      if (isMealPlanOrder) {
        refetchMealPlanData();
      }
      setDisabled(true);
    }
  }, [subscription.id, updateSubscription, tracker]);

  const text = match({ loading, disabled })
    .with({ loading: false, disabled: false }, () => 'Cancel')
    .with({ loading: false, disabled: true }, () => 'Cancelled')
    .otherwise(() => 'Loading');
  return (
    <OrdersCard header={<ConsumerSubscriptionsCardHeader subscription={subscription} />}>
      <ConsumerOrdersCardBody orders={[subscription.orderTemplate]} />
      <OrdersListRowButtonContainer>
        {isMealPlanOrder && (
          <EditButtonStyled
            onClick={() => {
              history.push(Routes.CONSUMER_MEAL_PLANS_MANAGE);
            }}>
            <EditButtonIcon />
            Manage Plan
          </EditButtonStyled>
        )}
        {!isMealPlanOrder && <CancelButtonStyled onClick={doCancel}>{text}</CancelButtonStyled>}
      </OrdersListRowButtonContainer>
    </OrdersCard>
  );
};
